/**
 * Route Plan Component
 * =====================
 * Daily route planning for delivery operations.
 * Features:
 * - Date picker (default today)
 * - Zone-based grouping of retailers
 * - Drag-and-drop reordering
 * - Vehicle type selection (Auto/Tempo)
 * - Save and Print functionality
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Truck, Save, Printer, RefreshCw, GripVertical, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '../../utils/api';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Retailer Row
const SortableRetailerRow = ({ retailer, index, zoneColor, isReadOnly }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: retailer.retailer_id, disabled: isReadOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-b hover:bg-gray-50 ${isDragging ? 'bg-blue-50' : ''}`}
    >
      <td className="px-2 py-2 text-center">
        {!isReadOnly && (
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded"
          >
            <GripVertical className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </td>
      <td className="px-3 py-2 text-sm text-center">{index + 1}</td>
      <td className="px-3 py-2 text-sm font-medium">{retailer.retailer_name}</td>
      <td className="px-3 py-2 text-sm text-gray-600">{retailer.area || '-'}</td>
      <td className="px-3 py-2 text-sm text-center font-semibold">{retailer.qty}</td>
      <td className="px-3 py-2 text-sm text-center">{retailer.assigned_to_name || 'Unassigned'}</td>
      <td className="px-3 py-2 text-sm text-center font-bold text-orange-600">{retailer.crate_count}</td>
    </tr>
  );
};

// Dragging Overlay Item
const DragOverlayItem = ({ retailer }) => (
  <div className="bg-white shadow-lg rounded p-3 border-2 border-blue-400">
    <div className="flex items-center gap-3">
      <GripVertical className="w-4 h-4 text-gray-400" />
      <span className="font-medium">{retailer?.retailer_name}</span>
      <span className="text-sm text-gray-500">({retailer?.qty} qty)</span>
    </div>
  </div>
);

// Zone Card Component
const ZoneCard = ({ 
  zone, 
  zoneIndex, 
  isReadOnly, 
  onVehicleChange, 
  onRetailersReorder,
  collapsedZones,
  toggleZoneCollapse 
}) => {
  const zoneColors = {
    'Pune Central': 'bg-blue-50 border-blue-200',
    'Pune East': 'bg-green-50 border-green-200',
    'Pune South': 'bg-yellow-50 border-yellow-200',
    'Pune West': 'bg-purple-50 border-purple-200',
    'Unassigned': 'bg-gray-50 border-gray-200',
  };
  
  const headerColors = {
    'Pune Central': 'bg-blue-100',
    'Pune East': 'bg-green-100',
    'Pune South': 'bg-yellow-100',
    'Pune West': 'bg-purple-100',
    'Unassigned': 'bg-gray-100',
  };

  const bgColor = zoneColors[zone.zone_name] || 'bg-gray-50 border-gray-200';
  const headerColor = headerColors[zone.zone_name] || 'bg-gray-100';
  const isCollapsed = collapsedZones[zone.zone_name];

  return (
    <div className={`rounded-lg border-2 ${bgColor} overflow-hidden mb-4`}>
      {/* Zone Header */}
      <div 
        className={`${headerColor} px-4 py-3 flex items-center justify-between cursor-pointer`}
        onClick={() => toggleZoneCollapse(zone.zone_name)}
      >
        <div className="flex items-center gap-4">
          <button className="p-1">
            {isCollapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
          </button>
          <h3 className="text-lg font-bold">{zone.zone_name}</h3>
          <span className="text-sm text-gray-600">
            {zone.retailers.length} retailers | {zone.total_qty} qty | {zone.total_crates} crates | <span className="font-semibold text-blue-600">{zone.retailers.length > 0 ? Math.round(zone.total_qty / zone.retailers.length) : 0} avg/retailer</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium">Suggested Vehicle:</span>
          <select
            value={zone.vehicle_type}
            onChange={(e) => onVehicleChange(zoneIndex, e.target.value)}
            disabled={isReadOnly}
            className={`px-3 py-1.5 rounded-lg border font-semibold ${
              zone.vehicle_type === 'Auto' 
                ? 'bg-green-100 border-green-300 text-green-800' 
                : 'bg-orange-100 border-orange-300 text-orange-800'
            } ${isReadOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="Auto">🛺 Auto</option>
            <option value="Tempo">🚛 Tempo</option>
          </select>
        </div>
      </div>

      {/* Zone Content - Retailers Table */}
      {!isCollapsed && (
        <div className="p-3">
          <table className="w-full">
            <thead>
              <tr className="bg-white/50 text-xs text-gray-600 uppercase">
                <th className="px-2 py-2 w-10"></th>
                <th className="px-3 py-2 text-center w-16">S.No</th>
                <th className="px-3 py-2 text-left">Retailer Name</th>
                <th className="px-3 py-2 text-left">Area</th>
                <th className="px-3 py-2 text-center w-20">Qty</th>
                <th className="px-3 py-2 text-center">Assigned To</th>
                <th className="px-3 py-2 text-center w-24">Crates</th>
              </tr>
            </thead>
            <SortableContext
              items={zone.retailers.map(r => r.retailer_id)}
              strategy={verticalListSortingStrategy}
            >
              <tbody>
                {zone.retailers.map((retailer, idx) => (
                  <SortableRetailerRow
                    key={retailer.retailer_id}
                    retailer={retailer}
                    index={idx}
                    zoneColor={bgColor}
                    isReadOnly={isReadOnly}
                  />
                ))}
              </tbody>
            </SortableContext>
          </table>
        </div>
      )}
    </div>
  );
};

// Main Route Plan Component
const RoutePlan = ({ userRole = 'admin' }) => {
  const [date, setDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [routePlan, setRoutePlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [collapsedZones, setCollapsedZones] = useState({});

  // Check if date is historical (read-only for non-admin)
  const today = new Date().toISOString().split('T')[0];
  const isHistorical = date < today;
  const isReadOnly = isHistorical && userRole !== 'admin';

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load route plan for selected date
  const loadRoutePlan = useCallback(async (regenerate = false) => {
    setLoading(true);
    try {
      const url = regenerate 
        ? `/api/route-plan?date=${date}&regenerate=true`
        : `/api/route-plan?date=${date}`;
      const response = await api.get(url);
      setRoutePlan(response.data);
      setHasChanges(false);
      if (regenerate) {
        toast.success('Route plan regenerated from latest indent data');
      }
    } catch (error) {
      console.error('Error loading route plan:', error);
      toast.error('Failed to load route plan');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    loadRoutePlan();
  }, [loadRoutePlan]);

  // Toggle zone collapse
  const toggleZoneCollapse = (zoneName) => {
    setCollapsedZones(prev => ({
      ...prev,
      [zoneName]: !prev[zoneName]
    }));
  };

  // Handle vehicle type change
  const handleVehicleChange = (zoneIndex, newVehicle) => {
    setRoutePlan(prev => {
      const updated = { ...prev };
      updated.zones = [...updated.zones];
      updated.zones[zoneIndex] = {
        ...updated.zones[zoneIndex],
        vehicle_type: newVehicle
      };
      return updated;
    });
    setHasChanges(true);
  };

  // Find which zone contains a retailer
  const findZoneByRetailerId = (retailerId) => {
    for (let i = 0; i < routePlan.zones.length; i++) {
      const idx = routePlan.zones[i].retailers.findIndex(r => r.retailer_id === retailerId);
      if (idx !== -1) {
        return { zoneIndex: i, retailerIndex: idx };
      }
    }
    return null;
  };

  // Handle drag start
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const activeLocation = findZoneByRetailerId(active.id);
    const overLocation = findZoneByRetailerId(over.id);

    if (!activeLocation || !overLocation) return;

    setRoutePlan(prev => {
      const updated = { ...prev };
      updated.zones = updated.zones.map(z => ({ ...z, retailers: [...z.retailers] }));

      if (activeLocation.zoneIndex === overLocation.zoneIndex) {
        // Same zone - reorder
        const zone = updated.zones[activeLocation.zoneIndex];
        zone.retailers = arrayMove(
          zone.retailers,
          activeLocation.retailerIndex,
          overLocation.retailerIndex
        );
        // Update order numbers
        zone.retailers.forEach((r, i) => r.order = i + 1);
      } else {
        // Different zones - move retailer
        const sourceZone = updated.zones[activeLocation.zoneIndex];
        const destZone = updated.zones[overLocation.zoneIndex];
        const [movedRetailer] = sourceZone.retailers.splice(activeLocation.retailerIndex, 1);
        destZone.retailers.splice(overLocation.retailerIndex, 0, movedRetailer);
        
        // Update order numbers and totals
        sourceZone.retailers.forEach((r, i) => r.order = i + 1);
        destZone.retailers.forEach((r, i) => r.order = i + 1);
        
        // Recalculate zone totals
        sourceZone.total_qty = sourceZone.retailers.reduce((sum, r) => sum + r.qty, 0);
        sourceZone.total_crates = sourceZone.retailers.reduce((sum, r) => sum + r.crate_count, 0);
        destZone.total_qty = destZone.retailers.reduce((sum, r) => sum + r.qty, 0);
        destZone.total_crates = destZone.retailers.reduce((sum, r) => sum + r.crate_count, 0);
      }

      return updated;
    });
    setHasChanges(true);
  };

  // Get the active retailer for drag overlay
  const getActiveRetailer = () => {
    if (!activeId || !routePlan) return null;
    for (const zone of routePlan.zones) {
      const retailer = zone.retailers.find(r => r.retailer_id === activeId);
      if (retailer) return retailer;
    }
    return null;
  };

  // Save route plan
  const handleSave = async () => {
    if (!routePlan || !hasChanges) return;

    setSaving(true);
    try {
      await api.post('/api/route-plan', {
        date: routePlan.date,
        zones: routePlan.zones
      });
      toast.success('Route plan saved successfully');
      setHasChanges(false);
      loadRoutePlan(); // Reload to get server state
    } catch (error) {
      console.error('Error saving route plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to save route plan');
    } finally {
      setSaving(false);
    }
  };

  // Print route plan
  const handlePrint = () => {
    if (!routePlan || routePlan.zones.length === 0) {
      toast.error('No data to print');
      return;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Route Plan - ${routePlan.date}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; margin-bottom: 5px; }
          .date { text-align: center; color: #666; margin-bottom: 20px; }
          .zone { margin-bottom: 30px; page-break-inside: avoid; }
          .zone-header { background: #f0f0f0; padding: 10px; margin-bottom: 10px; border-radius: 5px; }
          .zone-header h2 { margin: 0; display: inline; }
          .vehicle { float: right; font-weight: bold; padding: 5px 15px; border-radius: 4px; }
          .vehicle.auto { background: #d1fae5; color: #065f46; }
          .vehicle.tempo { background: #fed7aa; color: #9a3412; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #f5f5f5; }
          .center { text-align: center; }
          .total-row { font-weight: bold; background: #f9f9f9; }
          @media print {
            .zone { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>Route Plan</h1>
        <div class="date">${new Date(routePlan.date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
        
        ${routePlan.zones.map(zone => `
          <div class="zone">
            <div class="zone-header">
              <h2>${zone.zone_name}</h2>
              <span class="vehicle ${zone.vehicle_type.toLowerCase()}">${zone.vehicle_type === 'Auto' ? '🛺' : '🚛'} ${zone.vehicle_type}</span>
            </div>
            <table>
              <thead>
                <tr>
                  <th class="center" style="width: 50px">S.No</th>
                  <th>Retailer Name</th>
                  <th>Area</th>
                  <th class="center" style="width: 60px">Qty</th>
                  <th>Assigned To</th>
                  <th class="center" style="width: 70px">Crates</th>
                </tr>
              </thead>
              <tbody>
                ${zone.retailers.map((r, i) => `
                  <tr>
                    <td class="center">${i + 1}</td>
                    <td>${r.retailer_name}</td>
                    <td>${r.area || '-'}</td>
                    <td class="center">${r.qty}</td>
                    <td>${r.assigned_to_name || 'Unassigned'}</td>
                    <td class="center">${r.crate_count}</td>
                  </tr>
                `).join('')}
                <tr class="total-row">
                  <td colspan="3">Zone Total</td>
                  <td class="center">${zone.total_qty}</td>
                  <td></td>
                  <td class="center">${zone.total_crates}</td>
                </tr>
              </tbody>
            </table>
          </div>
        `).join('')}
        
        <div style="margin-top: 30px; padding-top: 10px; border-top: 2px solid #333;">
          <strong>Grand Total:</strong> 
          ${routePlan.zones.reduce((sum, z) => sum + z.retailers.length, 0)} retailers |
          ${routePlan.zones.reduce((sum, z) => sum + z.total_qty, 0)} qty |
          ${routePlan.zones.reduce((sum, z) => sum + z.total_crates, 0)} crates
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-gray-500" />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadRoutePlan(false)}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (window.confirm('This will discard any saved changes and regenerate from latest indent data. Continue?')) {
                loadRoutePlan(true);
              }
            }}
            disabled={loading}
            className="text-orange-600 border-orange-300 hover:bg-orange-50"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Regenerate
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          {isReadOnly && (
            <span className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded">
              Historical data (read-only)
            </span>
          )}
          {hasChanges && !isReadOnly && (
            <span className="text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded">
              Unsaved changes
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            disabled={!routePlan || routePlan.zones.length === 0}
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || saving || isReadOnly}
            className="bg-green-600 hover:bg-green-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      )}

      {/* No Data */}
      {!loading && routePlan && routePlan.zones.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <Truck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">{routePlan.message || 'No route plan data for this date'}</p>
        </div>
      )}

      {/* Route Plan Content */}
      {!loading && routePlan && routePlan.zones.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="space-y-4">
            {/* Summary Bar */}
            <div className="bg-white p-4 rounded-lg shadow-sm flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Zones:</span>
                <span className="font-bold text-lg">{routePlan.zones.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Retailers:</span>
                <span className="font-bold text-lg">
                  {routePlan.zones.reduce((sum, z) => sum + z.retailers.length, 0)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Total Qty:</span>
                <span className="font-bold text-lg">
                  {routePlan.zones.reduce((sum, z) => sum + z.total_qty, 0)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Total Crates:</span>
                <span className="font-bold text-lg text-orange-600">
                  {routePlan.zones.reduce((sum, z) => sum + z.total_crates, 0)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Avg/Retailer:</span>
                <span className="font-bold text-lg text-blue-600">
                  {(() => {
                    const totalRetailers = routePlan.zones.reduce((sum, z) => sum + z.retailers.length, 0);
                    const totalQty = routePlan.zones.reduce((sum, z) => sum + z.total_qty, 0);
                    return totalRetailers > 0 ? Math.round(totalQty / totalRetailers) : 0;
                  })()}
                </span>
              </div>
              <div className="flex items-center gap-4 ml-auto">
                <span className="text-sm flex items-center gap-1">
                  <span className="inline-block w-3 h-3 bg-green-200 rounded"></span>
                  Auto: {routePlan.zones.filter(z => z.vehicle_type === 'Auto').length}
                </span>
                <span className="text-sm flex items-center gap-1">
                  <span className="inline-block w-3 h-3 bg-orange-200 rounded"></span>
                  Tempo: {routePlan.zones.filter(z => z.vehicle_type === 'Tempo').length}
                </span>
              </div>
            </div>

            {/* Zone Cards */}
            {routePlan.zones.map((zone, zoneIndex) => (
              <ZoneCard
                key={zone.zone_name}
                zone={zone}
                zoneIndex={zoneIndex}
                isReadOnly={isReadOnly}
                onVehicleChange={handleVehicleChange}
                collapsedZones={collapsedZones}
                toggleZoneCollapse={toggleZoneCollapse}
              />
            ))}
          </div>

          <DragOverlay>
            {activeId ? <DragOverlayItem retailer={getActiveRetailer()} /> : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Help Text */}
      {!isReadOnly && routePlan && routePlan.zones.length > 0 && (
        <div className="text-xs text-gray-500 text-center">
          💡 Tip: Drag retailers to reorder within a zone or move between zones. Changes are saved when you click Save.
        </div>
      )}
    </div>
  );
};

export default RoutePlan;
